<script setup>
import { computed } from 'vue';
import { usePersonMeta } from '@/js/core/composables/usePersonMeta';
import { AUDIENCE_GROUP_DESCRIPTION } from '@/js/core/constants/audience-group-description';

const props = defineProps({
  person: {
    type: Object,
    required: true,
  },
});

const {
  departmentText,
  researchAreasText,
  titlesText,
  officesText,
  programsText,
  hasDirectoryBox: hasDirectoryBox,
  hasContactBox,
  phoneHref,
} = usePersonMeta(() => props.person);

const contacts = computed(() => props.person.contacts ?? []);

// Audience group only appears on your own mini-profile
const hasAudienceGroupBox = computed(
  () => props.person.isCurrentUser && !!props.person.audienceLabel
);
</script>

<template>
  <div class="person-profile-modal__meta-info-container">
    <ul class="person-profile-modal__meta-info">
      <li v-if="hasDirectoryBox" class="person-profile-modal__meta-info-row">
        <template v-if="departmentText">
          <span class="person-profile-modal__info-row-heading">Department</span>
          <span class="person-profile-modal__info-row-content">{{ departmentText }}</span>
        </template>
        <template v-if="researchAreasText">
          <span class="person-profile-modal__info-row-heading">Research Areas</span>
          <span class="person-profile-modal__info-row-content">{{ researchAreasText }}</span>
        </template>
        <template v-if="titlesText">
          <span class="person-profile-modal__info-row-heading">Titles</span>
          <span class="person-profile-modal__info-row-content">{{ titlesText }}</span>
        </template>
        <template v-if="programsText">
          <span class="person-profile-modal__info-row-heading">Programs</span>
          <span class="person-profile-modal__info-row-content">{{ programsText }}</span>
        </template>
      </li>

      <li v-if="hasAudienceGroupBox" class="person-profile-modal__meta-info-row">
        <span class="person-profile-modal__info-row-heading">Audience Group</span>
        <span class="person-profile-modal__info-row-content">
          <span class="person-profile-modal__role">{{ person.audienceLabel }}</span>
        </span>
        <p class="person-profile-modal__role-description">{{ AUDIENCE_GROUP_DESCRIPTION }}</p>
      </li>

      <li
        v-if="hasContactBox"
        class="person-profile-modal__meta-info-row"
        :class="{ 'last-box': !contacts.length }"
      >
        <template v-if="officesText">
          <span class="person-profile-modal__info-row-heading">Office</span>
          <span class="person-profile-modal__info-row-content">{{ officesText }}</span>
        </template>
        <template v-if="person.officeAddress">
          <span class="person-profile-modal__info-row-heading">Office Address</span>
          <span class="person-profile-modal__info-row-content">{{ person.officeAddress }}</span>
        </template>
        <template v-if="person.email">
          <span class="person-profile-modal__info-row-heading">Email</span>
          <span class="person-profile-modal__info-row-content">
            <a :href="`mailto:${person.email}`" class="person-profile-modal__contact-link">{{
              person.email
            }}</a>
          </span>
        </template>
        <template v-if="person.phone">
          <span class="person-profile-modal__info-row-heading">Phone</span>
          <span class="person-profile-modal__info-row-content">
            <a :href="phoneHref" class="person-profile-modal__contact-link">{{ person.phone }}</a>
          </span>
        </template>
      </li>

      <li v-if="contacts.length" class="person-profile-modal__meta-info-row last-box">
        <template v-for="contact in contacts" :key="contact.uuid">
          <span class="person-profile-modal__info-row-heading">{{ contact.label }}</span>
          <span class="person-profile-modal__info-row-content">{{ contact.value }}</span>
        </template>
      </li>
    </ul>
  </div>
</template>

<style scoped></style>
